import { Box, Button, Checkbox, IconButton, TextField } from "@material-ui/core"
import Close from "@material-ui/icons/Close"
import Info from "@material-ui/icons/Info"
import type { AutocompleteRenderInputParams, AutocompleteRenderOptionState } from "@material-ui/lab"
import { Autocomplete } from "@material-ui/lab"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { ChangeEvent, ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { config } from "../../../config"
import { useBrandName } from "../../../hooks"
import type { UserGroupDto } from "../../../models"
import type { AppState, AppThunkDispatch, Project, SnackbarAction, UserGroupListAction } from "../../../redux"
import { fetchUserGroups, pushMessage } from "../../../redux"
import { mapObjectDescriptorToTranslation } from "../../../utils"
import { Body2, LinkButton, NotSavedWarning, SubTitle1 } from "../../atoms"
import { DialogWindow, DialogWindowHeader } from "../../molecules"

import "./share-dialog.styles.scss"

export interface IShareDialogProps {
  open: boolean
  shouldSave?: boolean
  objectDescriptor: "comparison" | "map"
  onSave: () => void
  onShare: (groups: UserGroupDto[], project: Project) => void
  onClose: () => void
}

export interface IShareDialogStateProps {
  groups?: UserGroupDto[]
  project?: Project
  fetchGroups: () => void
  pushSuccessMessage: (text: string) => void
}

export function ShareDialogView(props: IShareDialogProps & IShareDialogStateProps): ReactElement {
  const {
    open,
    project,
    shouldSave,
    objectDescriptor,
    groups,
    onSave,
    onShare,
    onClose,
    fetchGroups,
    pushSuccessMessage
  } = props

  const { t } = useTranslation(["shareMapClass", "general"])
  const brandName: string = useBrandName()

  const [selectedGroups, setSelectedGroups] = useState<UserGroupDto[]>([])
  const [shareInfoClosed, setShareInfoClosed] = useState<boolean>(false)
  const [dontShowChecked, setDontShowChecked] = useState<boolean>(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const shouldShowShareInfo: boolean = useMemo<boolean>(() => {
    const localStorageItem = localStorage.getItem(config.hideShareDialogWarningLocalstorageKey)
    if (localStorageItem !== null && localStorageItem === "true") return false
    return !shareInfoClosed && !shouldSave
  }, [shareInfoClosed])

  const isGroupSelected: (group: UserGroupDto) => boolean = useCallback(
    (group: UserGroupDto) => {
      return selectedGroups?.some((g: UserGroupDto) => g.id === group.id)
    },
    [selectedGroups]
  )
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchGroups()
  }, [])

  function onGroupChange(_: ChangeEvent<{}>, groups: UserGroupDto[]) {
    if (groups.length <= 1) {
      setSelectedGroups(groups)
      return
    }
    const lastGroup: UserGroupDto = {
      ...groups[groups.length - 1]
    }
    const lastIsDuplicate: boolean = groups.filter((group: UserGroupDto) => group.id === lastGroup.id).length > 1
    if (!lastIsDuplicate) {
      setSelectedGroups(groups)
      return
    }

    const newGroups: UserGroupDto[] = groups.filter((group: UserGroupDto) => group.id !== lastGroup.id)
    setSelectedGroups(newGroups)
  }

  function onShareClicked() {
    if (selectedGroups.length > 0 && project) onShare(selectedGroups, project)
    if (dontShowChecked) localStorage.setItem(config.hideShareDialogWarningLocalstorageKey, "true")
    onClose()
  }

  function handleCopyClick() {
    navigator.clipboard.writeText(window.location.href)
    pushSuccessMessage(t("shareMapClass:linkCopied"))
  }

  function getRenderOptions(option: UserGroupDto, _: AutocompleteRenderOptionState): ReactElement {
    return (
      <Box className="share-dialog-group-selector-option">
        <Checkbox color="primary" checked={isGroupSelected(option)} />
        <Body2>{option.name}</Body2>
      </Box>
    )
  }

  function closeShareInfo(): void {
    setShareInfoClosed(true)
  }

  function onDontShowClicked(): void {
    setDontShowChecked(!dontShowChecked)
  }

  return (
    <DialogWindow open={open} onClose={onClose} className="share-dialog">
      <DialogWindowHeader title={t("shareMapClass:shareMap")} onClose={onClose} />
      {shouldSave && <NotSavedWarning text={t("shareMapClass:saveMapBeforeShare", { brandName })} onSave={onSave} />}
      <Box className="share-dialog-content">
        <Body2>{t("shareMapClass:selectClassesShare")}</Body2>
        <Box>
          <SubTitle1>
            <b>{t("shareMapClass:shareWithClass")}</b>
          </SubTitle1>
        </Box>
        <Autocomplete
          disabled={shouldSave}
          renderOption={getRenderOptions}
          multiple
          options={groups ? groups : []}
          getOptionLabel={(option: UserGroupDto) => option.name!}
          onChange={onGroupChange}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} variant="outlined" placeholder={t("shareMapClass:selectClass")} />
          )}
        />
        <Box className="share-via-link">
          <SubTitle1>
            {t("shareMapClass:linkType", {
              type: mapObjectDescriptorToTranslation(objectDescriptor)
            })}
          </SubTitle1>
          <TextField
            value={window.location.href}
            className={`assignment-link ${shouldSave ? "disabled" : ""}`}
            disabled={shouldSave}
            fullWidth
            variant="outlined"
            InputProps={{
              endAdornment: (
                <Button disabled={shouldSave} onClick={handleCopyClick} variant="contained" color="primary">
                  {t("general:copy")}
                </Button>
              )
            }}
          />
        </Box>
      </Box>
      {shouldShowShareInfo && (
        <Box className="share-info-container">
          <Box className="share-info">
            <Info className="info-icon" />
            <Body2>
              {t("shareMapClass:sharedMapsSavedInAtlas", {
                brandName
              })}
            </Body2>
            <IconButton className="share-info-close-button" onClick={closeShareInfo}>
              <Close />
            </IconButton>
          </Box>
          <Box className="dont-show">
            <Checkbox checked={dontShowChecked} onClick={onDontShowClicked} color="primary" />
            <Body2>{t("general:dontShowMessage")}</Body2>
          </Box>
        </Box>
      )}
      <Box className="share-dialog-footer">
        <LinkButton onClick={onClose}>{t("general:dialog.cancel")}</LinkButton>
        <Button
          variant="contained"
          color="primary"
          onClick={onShareClicked}
          disabled={selectedGroups.length < 1 || shouldSave}
        >
          {t("shareMapClass:shareMap")}
        </Button>
      </Box>
    </DialogWindow>
  )
}

function mapStateToProps(state: AppState) {
  return {
    groups: state.user.groups,
    project: state.project.present.project
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<UserGroupListAction | SnackbarAction>) {
  return {
    fetchGroups: () => dispatch(fetchUserGroups()),
    pushSuccessMessage: (text: string) => dispatch(pushMessage(text))
  }
}

export const ShareDialog = connect(mapStateToProps, mapDispatchToProps)(ShareDialogView)
